import React, { useEffect, useMemo, useState } from "react";
import Icon from "../Icon";
import PillSelect from "./PillSelect";
import MockTicketSidePanel from "./MockTicketSidePanel";
import {
    MOCK_ASSIGNEE_OPTIONS,
    MOCK_TYPES,
    MOCK_TAGS,
    TICKET_PRIORITY_OPTIONS,
    TICKET_STATUS_OPTIONS,
    buildDepartmentOptions,
    getMockUserInitials,
} from "./mockData";

const STATUS_OPTIONS = TICKET_STATUS_OPTIONS.filter((o) => o.value).map((o) => ({
    value: o.value,
    label: o.label,
    color: o.color,
}));

const getCustomerInitials = (name) => {
    if (!name || name === "guest") return "GU";
    return getMockUserInitials(name);
};

const getCustomerEmail = (ticket) => {
    if (ticket.customerEmail) return ticket.customerEmail;
    if (ticket.createdBy === "guest") return "guest@example.com";
    return `${ticket.createdBy.split(" ")[0].toLowerCase()}@example.com`;
};

const MockTicketDetail = ({
    ticket,
    tickets = [],
    departments,
    assigneeOptions = MOCK_ASSIGNEE_OPTIONS,
    onBack,
    onSelectTicket,
    onStatusChange,
    onDepartmentChange,
    onAssigneeChange,
}) => {
    const [showPanel, setShowPanel] = useState(() => {
        if (typeof window === "undefined") return true;
        return window.matchMedia("(min-width: 768px)").matches;
    });
    const [composeTab, setComposeTab] = useState("reply");
    const [contentTab, setContentTab] = useState("notes");
    const [propertiesOpen, setPropertiesOpen] = useState(true);
    const [timeLogsOpen, setTimeLogsOpen] = useState(false);
    const [reply, setReply] = useState("");
    const [forwardTo, setForwardTo] = useState("");
    const [cc, setCc] = useState("");
    const [replies, setReplies] = useState([]);
    const [notes, setNotes] = useState([]);
    const [priorityKey, setPriorityKey] = useState(ticket?.priorityKey || "medium");
    const [typeKey, setTypeKey] = useState("");
    const [tagKey, setTagKey] = useState("");
    const [dueDate, setDueDate] = useState("");
    const [showActivity, setShowActivity] = useState(false);

    useEffect(() => {
        setReplies(ticket?.replies || []);
        setNotes(ticket?.notes || []);
        setReply("");
        setForwardTo("");
        setCc("");
        setPriorityKey(ticket?.priorityKey || "medium");
        setTypeKey(ticket?.typeKey || "");
        setTagKey(ticket?.tagKey || "");
        setDueDate(ticket?.dueDate || "");
        setComposeTab("reply");
        setContentTab("notes");
    }, [ticket?.id]);

    const departmentOptions = useMemo(
        () => buildDepartmentOptions(departments),
        [departments]
    );

    if (!ticket) return null;

    const ticketNumber = ticket.ticketNumber || `TKT-${String(ticket.id).padStart(9, "0")}`;
    const customerName = ticket.customerName || ticket.createdBy;
    const customerInitials = ticket.customerInitials || getCustomerInitials(customerName);
    const customerEmail = getCustomerEmail(ticket);
    const displayDate = ticket.displayDate || ticket.created;
    const description = ticket.description || ticket.preview;

    const postCompose = () => {
        if (!reply.trim()) return;
        if (composeTab === "forward" && !forwardTo.trim()) return;

        const entry = {
            id: `${Date.now()}`,
            from: composeTab === "note" ? "agent" : composeTab,
            author: "Cole Lockwood",
            initials: "CL",
            text: reply.trim(),
            date: new Date().toLocaleString(),
        };

        if (composeTab === "note") {
            setNotes((prev) => [...prev, entry]);
            setContentTab("notes");
        } else {
            setReplies((prev) => [...prev, entry]);
            setContentTab("replies");
        }
        setReply("");
    };

    return (
        <div className="clk-mk-detail-layout">
            <div className={`clk-mk-detail-panel-wrap${showPanel ? " is-open" : ""}`}>
                {showPanel ? (
                    <>
                        <MockTicketSidePanel
                            tickets={tickets}
                            currentId={ticket.id}
                            onSelect={onSelectTicket}
                        />
                        <button
                            type="button"
                            className="clk-mk-ticket-panel-close"
                            onClick={() => setShowPanel(false)}
                            aria-label="Close ticket list"
                            title="Close ticket list"
                        >
                            <Icon name="chevron-left" size={13} />
                        </button>
                    </>
                ) : (
                    <div className="clk-mk-detail-panel-collapsed">
                        <button
                            type="button"
                            className="clk-mk-detail-panel-open"
                            onClick={() => setShowPanel(true)}
                            aria-label="Open ticket list"
                        />
                        <button
                            type="button"
                            className="clk-mk-ticket-panel-open"
                            onClick={() => setShowPanel(true)}
                            aria-label="Open ticket list"
                            title="Open ticket list"
                        >
                            <Icon name="chevron-right" size={13} />
                        </button>
                    </div>
                )}
            </div>

            <div className="clk-mk-detail-main">
                <div className="clk-mk-detail-toolbar">
                    <button type="button" className="clk-mk-back" onClick={onBack}>
                        ← Back to tickets
                    </button>
                    <div className="clk-mk-detail-actions">
                        <button
                            type="button"
                            className="clk-mk-detail-action clk-mk-detail-action--warn"
                            disabled
                            title="Not available in demo"
                        >
                            Ask for Clarification
                        </button>
                        <button
                            type="button"
                            className="clk-mk-detail-action clk-mk-detail-action--warn"
                            disabled
                            title="Not available in demo"
                        >
                            Ask for Confirmation
                        </button>
                        <button
                            type="button"
                            className="clk-mk-detail-action clk-mk-detail-action--info"
                            onClick={() => setShowActivity(true)}
                        >
                            <Icon name="clock" size={14} />
                            Ticket Activity
                        </button>
                        <button
                            type="button"
                            className="clk-mk-detail-action clk-mk-detail-action--icon"
                            aria-label="More actions"
                            disabled
                            title="Not available in demo"
                        >
                            <Icon name="menu" size={14} />
                        </button>
                    </div>
                </div>

                <div className="clk-mk-detail-grid">
                    <div className="clk-mk-detail-content">
                        <div className="clk-mk-conversation-ticket">
                            <div className="clk-mk-conversation-user">
                                <span className="clk-mk-conversation-avatar">{customerInitials}</span>
                                <span>{customerName}</span>
                            </div>
                            <div className="clk-mk-conversation-body">
                                <div className="clk-mk-conversation-head">
                                    <strong>{ticket.title}</strong>
                                    <span>#{ticketNumber}</span>
                                </div>
                                <div className="clk-mk-conversation-message">{description}</div>
                                <div className="clk-mk-conversation-date">{displayDate}</div>
                            </div>
                        </div>

                        <div className="clk-mk-compose-tabs">
                            {[
                                { id: "reply", label: "Reply", icon: "chat" },
                                { id: "forward", label: "Forward", icon: "arrow" },
                                { id: "note", label: "Add Note", icon: "card" },
                            ].map((tab) => (
                                <button
                                    key={tab.id}
                                    type="button"
                                    className={composeTab === tab.id ? "is-active" : ""}
                                    onClick={() => setComposeTab(tab.id)}
                                >
                                    <Icon name={tab.icon} size={14} />
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        <div className="clk-mk-compose-panel">
                            {composeTab !== "note" && (
                                <div className="clk-mk-compose-recipients">
                                    <label className="clk-mk-compose-recipient-row">
                                        <span className="clk-mk-compose-recipient-label">To:</span>
                                        <input
                                            type="text"
                                            value={composeTab === "forward" ? forwardTo : customerEmail}
                                            onChange={(e) => composeTab === "forward" && setForwardTo(e.target.value)}
                                            readOnly={composeTab === "reply"}
                                        />
                                    </label>
                                    <label className="clk-mk-compose-recipient-row">
                                        <span className="clk-mk-compose-recipient-label">CC:</span>
                                        <input
                                            type="text"
                                            value={cc}
                                            onChange={(e) => setCc(e.target.value)}
                                            placeholder=""
                                        />
                                    </label>
                                </div>
                            )}
                            <textarea
                                className="clk-mk-compose-editor"
                                placeholder="Please add your reply here."
                                value={reply}
                                onChange={(e) => setReply(e.target.value)}
                                rows={5}
                            />
                            <div className="clk-mk-compose-footer">
                                <button type="button" className="clk-mk-compose-attach" aria-label="Attach file">
                                    <Icon name="link" size={16} />
                                </button>
                                <button type="button" className="clk-mk-compose-submit" onClick={postCompose}>
                                    {composeTab === "note" ? "Post Note" : composeTab === "forward" ? "Forward" : "Post Reply"}
                                </button>
                            </div>
                        </div>

                        <div className="clk-mk-content-tabs">
                            {[
                                { id: "replies", label: "Conversations" },
                                { id: "notes", label: "Notes" },
                                { id: "claritask", label: "Claritask Comments" },
                                { id: "linked", label: "Linked Claritask" },
                            ].map((tab) => (
                                <button
                                    key={tab.id}
                                    type="button"
                                    className={contentTab === tab.id ? "is-active" : ""}
                                    onClick={() => setContentTab(tab.id)}
                                >
                                    {tab.id === "linked" && (
                                        <img src="/images/claritasklogo.svg" alt="" className="clk-mk-tab-logo" />
                                    )}
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        <div className="clk-mk-content-panel">
                            {contentTab === "replies" && (
                                replies.length ? replies.map((r) => (
                                    <div key={r.id} className="clk-mk-thread-item clk-mk-thread-item--agent">
                                        <span className="clk-mk-thread-avatar">{r.initials || "CL"}</span>
                                        <div className="clk-mk-thread-bubble">
                                            <strong>{r.author || "Agent"}</strong>
                                            <p>{r.text}</p>
                                            <span>{r.date}</span>
                                        </div>
                                    </div>
                                )) : <p className="clk-mk-empty-tab">No conversations yet</p>
                            )}
                            {contentTab === "notes" && (
                                notes.length ? notes.map((n) => (
                                    <div key={n.id} className="clk-mk-thread-item clk-mk-thread-item--note">
                                        <span className="clk-mk-thread-avatar">{n.initials || "CL"}</span>
                                        <div className="clk-mk-thread-bubble">
                                            <strong>{n.author || "Agent"}</strong>
                                            <p>{n.text}</p>
                                            <span>{n.date}</span>
                                        </div>
                                    </div>
                                )) : <p className="clk-mk-empty-tab">No notes yet</p>
                            )}
                            {contentTab === "claritask" && (
                                <p className="clk-mk-empty-tab">No Claritask comments yet</p>
                            )}
                            {contentTab === "linked" && (
                                <div className="clk-mk-linked-empty">
                                    <img src="/images/claritasklogo.svg" alt="Claritask" />
                                    <p>No linked Claritask tasks yet.</p>
                                    <button type="button" className="clk-mk-linked-btn">Link existing task</button>
                                    <button type="button" className="clk-mk-linked-btn clk-mk-linked-btn--primary">Create task</button>
                                </div>
                            )}
                        </div>
                    </div>

                    <aside className="clk-mk-detail-properties">
                        <div className="clk-mk-accordion">
                            <button
                                type="button"
                                className="clk-mk-accordion-head"
                                onClick={() => setPropertiesOpen((v) => !v)}
                            >
                                <Icon name="ticket" size={16} />
                                Ticket Properties
                                <span>{propertiesOpen ? "▴" : "▾"}</span>
                            </button>
                            {propertiesOpen && (
                                <div className="clk-mk-accordion-body">
                                    <label className="clk-mk-property">
                                        <span>Due Date</span>
                                        <input
                                            type="date"
                                            value={dueDate}
                                            onChange={(e) => setDueDate(e.target.value)}
                                        />
                                    </label>
                                    <label className="clk-mk-property">
                                        <span>Assignee</span>
                                        <PillSelect
                                            value={ticket.assignee || ""}
                                            options={assigneeOptions}
                                            onChange={(value) => onAssigneeChange(ticket.id, value)}
                                            ariaLabel="Ticket assignee"
                                            showDot={Boolean(ticket.assignee)}
                                        />
                                    </label>
                                    <label className="clk-mk-property">
                                        <span>Status</span>
                                        <PillSelect
                                            value={ticket.status}
                                            options={STATUS_OPTIONS}
                                            onChange={(value) => onStatusChange(ticket.id, value)}
                                            ariaLabel="Ticket status"
                                        />
                                    </label>
                                    <label className="clk-mk-property">
                                        <span>Department</span>
                                        <PillSelect
                                            value={ticket.department}
                                            options={departmentOptions}
                                            onChange={(value) => onDepartmentChange(ticket.id, value)}
                                            ariaLabel="Ticket department"
                                        />
                                    </label>
                                    <label className="clk-mk-property">
                                        <span>Priority</span>
                                        <PillSelect
                                            value={priorityKey}
                                            options={TICKET_PRIORITY_OPTIONS}
                                            onChange={setPriorityKey}
                                            ariaLabel="Ticket priority"
                                        />
                                    </label>
                                    <label className="clk-mk-property">
                                        <span>Type</span>
                                        <PillSelect
                                            value={typeKey}
                                            options={MOCK_TYPES}
                                            onChange={setTypeKey}
                                            ariaLabel="Ticket type"
                                            showDot={Boolean(typeKey)}
                                        />
                                    </label>
                                    <label className="clk-mk-property">
                                        <span>Tag</span>
                                        <PillSelect
                                            value={tagKey}
                                            options={MOCK_TAGS}
                                            onChange={setTagKey}
                                            ariaLabel="Ticket tag"
                                            showDot={Boolean(tagKey)}
                                        />
                                    </label>
                                </div>
                            )}
                        </div>

                        <div className="clk-mk-accordion">
                            <button
                                type="button"
                                className="clk-mk-accordion-head"
                                onClick={() => setTimeLogsOpen((v) => !v)}
                            >
                                <Icon name="clock" size={16} />
                                Time Logs
                                <span>{timeLogsOpen ? "▴" : "▾"}</span>
                            </button>
                            {timeLogsOpen && (
                                <div className="clk-mk-accordion-body clk-mk-accordion-body--muted">
                                    <p className="clk-mk-empty-tab">No time logged on this ticket.</p>
                                    <button
                                        type="button"
                                        className="clk-mk-time-start"
                                        disabled
                                        title="Not available in demo"
                                    >
                                        Start timer
                                    </button>
                                </div>
                            )}
                        </div>
                    </aside>
                </div>
            </div>

            {showActivity && (
                <div className="clk-mk-modal-backdrop" onClick={() => setShowActivity(false)}>
                    <div className="clk-mk-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="detail-activity-title">
                        <h5 id="detail-activity-title">Ticket activity</h5>
                        <p><strong>{ticket.title}</strong></p>
                        <ul className="clk-mk-activity-list">
                            <li>Ticket created on {displayDate}</li>
                            <li>Status: {ticket.statusLabel || ticket.status}</li>
                            {ticket.assignee && <li>Assigned to {ticket.assignee}</li>}
                            {replies.length > 0 && <li>{replies.length} repl{replies.length === 1 ? "y" : "ies"} posted</li>}
                            {notes.length > 0 && <li>{notes.length} internal note(s)</li>}
                        </ul>
                        <div className="clk-mk-create-actions">
                            <button type="button" className="clk-mk-primary-btn" onClick={() => setShowActivity(false)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MockTicketDetail;
